import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from './contexts/theme-context';
import { AuthProvider } from './contexts/auth-context';
import { Toaster } from './components/ui/toaster';
import Layout from './components/layout/Layout';

// Auth Components
import Login from './components/auth/Login';
import { PrivateRoute } from './components/auth/PrivateRoute';

// Page Components - matching Angular app structure
import Home from './components/pages/Home';
import TechnicalParameters from './components/pages/TechnicalParameters';
import PerformanceAnalytics from './components/pages/PerformanceAnalytics';
import EnergyLoss from './components/pages/EnergyLoss';
import Reports from './components/pages/Reports';
import AssetManagement from './components/pages/AssetManagement';
import PowerForecast from './components/pages/PowerForecast';
import InverterAsset from './components/pages/InverterAsset';
import WeatherSensor from './components/pages/WeatherSensor';
import HistoricalTechPar from './components/pages/HistoricalTechPar';
import AssetContainer from './components/pages/AssetContainer';
import InverterAvailability from './components/pages/InverterAvailability';
import StringCombinerBoxAvailability from './components/pages/StringCombinerBoxAvailability';
import TrackerAvailability from './components/pages/TrackerAvailability';

// Additional components we need to create to match Angular
// These will be created later but we'll add placeholder components for now
const PlantContainer = () => <Outlet />;
const PowerContainer = () => <Outlet />;
const PlantAvailabilityContainer = () => <Outlet />;
const HistoricalKpiData = () => <div>Historical KPI Data</div>;
const IntraDay = () => <PowerForecast type="intra" />;
const InterDay = () => <PowerForecast type="inter" />;
const WeekAhead = () => <PowerForecast type="week" />;
const MonthAhead = () => <PowerForecast type="month" />;
const HistoricalPower = () => <PowerForecast type="historical" />;
const AdminDetails = () => <div>Admin Details</div>;
const Register = () => <div>Register</div>;
const PasswordReset = () => <div>Password Reset</div>;
const Password = () => <div>Password</div>;

// Main App Component
const AppContent = () => {
  return (
    <Router>
      <Routes>
        {/* Default redirect to sign-in (matching Angular) */}
        <Route path="/" element={<Navigate to="/sign-in" replace />} />
        
        {/* Authentication Routes */}
        <Route path="/sign-in" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/password-reset" element={<PasswordReset />} />
        <Route path="/password" element={<Password />} />
        
        {/* Protected Routes */}
        <Route element={<PrivateRoute><Layout /></PrivateRoute>}>
          {/* Home Route */}
          <Route path="/home" element={<Home />} />
          
          {/* Plant Routes with nested structure */}
          <Route path="/plant" element={<PlantContainer />}>
            <Route index element={<Navigate to="/plant/techpar" replace />} />
            <Route path="techpar" element={<TechnicalParameters />} />
            <Route path="performpar" element={<PerformanceAnalytics />} />
            <Route path="histtechpar" element={<HistoricalTechPar />} />
            <Route path="summary" element={<TechnicalParameters />} />
            <Route path="historicalKpiData" element={<HistoricalKpiData />} />
          </Route>
          
          {/* Power Forecast Routes with nested structure */}
          <Route path="/power" element={<PowerContainer />}>
            <Route index element={<Navigate to="/power/intra" replace />} />
            <Route path="intra" element={<IntraDay />} />
            <Route path="inter" element={<InterDay />} />
            <Route path="week" element={<WeekAhead />} />
            <Route path="month" element={<MonthAhead />} />
            <Route path="historical-data" element={<HistoricalPower />} />
          </Route>
          
          {/* Energy Loss Route */}
          <Route path="/energy" element={<EnergyLoss />} />
          
          {/* Plant Availability Routes */}
          <Route path="/plant-availability" element={<PlantAvailabilityContainer />}>
            <Route index element={<Navigate to="/plant-availability/inverter" replace />} />
            <Route path="inverter" element={<InverterAvailability />} />
            <Route path="string-combiner-box" element={<StringCombinerBoxAvailability />} />
            <Route path="tracker" element={<TrackerAvailability />} />
          </Route>
          
          {/* Reports Route */}
          <Route path="/report" element={<Reports />} />
          
          {/* Asset Management Routes */}
          <Route path="/asset" element={<AssetManagement />} />
          <Route path="/assetpage" element={<AssetContainer />}>
            <Route path="inverter" element={<InverterAsset />} />
            <Route path="weathersensor" element={<WeatherSensor />} />
          </Route>
          
          {/* Admin Details Route */}
          <Route path="/admin_details" element={<AdminDetails />} />
        </Route>
        
        {/* Catch all redirect */}
        <Route path="*" element={<Navigate to="/sign-in" replace />} />
      </Routes>
    </Router>
  );
};

// Root App Component with Providers
const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
        <Toaster />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;